<template>
  <div class="webrtc-player">
    <video 
      ref="video" 
      autoplay 
      playsinline
      @loadedmetadata="onVideoLoadedMetadata"
      @playing="onVideoPlaying"
      @error="onVideoError"
    ></video>
    <div v-if="stream" class="vumeter-container">
      <Vumeter :mediaStream="stream" />
    </div>
    <button 
      v-if="stream" 
      class="mute-btn" 
      @click="toggleMute"
      :title="isMuted ? 'Unmute' : 'Mute'"
    >
      <span v-if="isMuted">🔇</span>
      <span v-else>🔊</span>
    </button>
  </div>
</template>

<script>
import WowzaPeerConnectionPlay from '/vodalys/vwebrtc3_common/services/WowzaWebrtcExemple/src/lib/WowzaPeerConnectionPlay.js';
import { mungeSDPPlay } from '/vodalys/vwebrtc3_common/services/WowzaWebrtcExemple/src/lib/WowzaMungeSDP.js';
import Vumeter from '/vodalys/vvvumeter/main/Vumeter.vue';

export default {
  components: {
    Vumeter,
  },
  props: {
    streamDescriptor: {
      type: Object,
      required: true,
      validator: (value) => {
        return value && value.host && (value.http_port || value.port) && value.application && value.stream_name;
      },
    },
    peerConfig: {
      type: Object,
      default: () => ({
        iceServers: [],
      }),
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      wowzaPeerConnection: null,
      destroyed: false,
      reconnectTimeout: null,
      stream: null,
      isMuted: false,
    };
  },
  computed: {
    wowzaConfig() {
      if (!this.streamDescriptor) return null;
      const port = this.streamDescriptor.http_port;
      const streamName = this.streamDescriptor.stream_name;
      const applicationName = this.streamDescriptor.application;
      
      // Construire le streamName avec play_key si présent
      let fullStreamName = streamName;
      if (this.streamDescriptor.stream_play_key) {
        fullStreamName = `${streamName}?play_key=${this.streamDescriptor.stream_play_key}`;
      }
      
      const config = {
        applicationName: applicationName,
        streamName: fullStreamName,
        wssUrl: `wss://${this.streamDescriptor.host}:${port}/webrtc-session.json`,
      };
      
      console.log('WebRTCPlayer: wowzaConfig computed', config);
      return config;
    },
  },
  watch: {
    streamDescriptor: {
      handler() {
        if (this.isPlaying()) {
          this.stop().then(() => {
            if (this.autoplay) {
              this.play();
            }
          });
        } else if (this.autoplay) {
          this.play();
        }
      },
      immediate: false,
    },
  },
  mounted() {
    if (this.autoplay && this.wowzaConfig) {
      this.play();
    }
  },
  beforeUnmount() {
    this.destroy();
  },
  methods: {
    // Custom mungeSDP that applies H.264 profile modification to offer SDP
    mungeSDP(sdpStr) {
      // Apply H.264 profile modification (stereo modification is applied to answer SDP in gotDescription)
      return mungeSDPPlay(sdpStr);
    },

    play() {
      console.log('WebRTCPlayer: play() called', {
        destroyed: this.destroyed,
        hasWowzaConfig: !!this.wowzaConfig,
        hasExistingConnection: !!this.wowzaPeerConnection,
      });

      if (this.destroyed) {
        console.warn('WebRTCPlayer: Component already destroyed');
        return;
      }
      if (!this.wowzaConfig) {
        console.warn('WebRTCPlayer: Missing wowzaConfig');
        return;
      }
      if (this.wowzaPeerConnection && this.wowzaPeerConnection.peerConnection && this.wowzaPeerConnection.peerConnection.connectionState !== 'closed') {
        console.log('WebRTCPlayer: Already playing, skipping');
        return Promise.resolve();
      }

      // Stop existing connection if any
      if (this.wowzaPeerConnection) {
        console.log('WebRTCPlayer: Stopping existing connection');
        this.wowzaPeerConnection.stop();
        this.wowzaPeerConnection = null;
      }

      const videoElement = this.$refs.video;
      if (!videoElement) {
        console.warn('WebRTCPlayer: Video element not found');
        return;
      }

      console.log('WebRTCPlayer: Video element found', {
        videoElement: !!videoElement,
        currentSrcObject: !!videoElement.srcObject,
      });

      // Build streamInfo with play_key if present
      const streamInfo = {
        applicationName: this.wowzaConfig.applicationName,
        streamName: this.wowzaConfig.streamName,
      };

      console.log('WebRTCPlayer: Creating WowzaPeerConnectionPlay instance', {
        sdpURL: this.wowzaConfig.wssUrl,
        streamInfo: streamInfo,
      });

      // Create WowzaPeerConnectionPlay instance
      this.wowzaPeerConnection = new WowzaPeerConnectionPlay({
        sdpURL: this.wowzaConfig.wssUrl,
        videoElement: videoElement,
        streamInfo: streamInfo,
        userData: [],
        mungeSDP: this.mungeSDP.bind(this),
        onconnectionstatechange: this._onConnectionStateChange.bind(this),
        onerror: this._onError.bind(this),
        onstop: this._onStop.bind(this),
      });

      // Override gotDescription to add stereo modification to answer SDP
      const originalGotDescription = this.wowzaPeerConnection.gotDescription.bind(this.wowzaPeerConnection);
      this.wowzaPeerConnection.gotDescription = (description) => {
        console.log('WebRTCPlayer: gotDescription called, applying stereo modification');
        // Apply stereo modification to answer SDP
        const originalSdp = description.sdp;
        description.sdp = description.sdp.replace(
          /minptime=10;useinbandfec=1/g,
          'minptime=10;useinbandfec=1;stereo=1;sprop-stereo=1'
        );
        if (originalSdp !== description.sdp) {
          console.log('WebRTCPlayer: Stereo modification applied to answer SDP');
        }
        originalGotDescription(description);
      };

      // Override gotRemoteTrack to emit Vue events and ensure video plays
      const originalGotRemoteTrack = this.wowzaPeerConnection.gotRemoteTrack.bind(this.wowzaPeerConnection);
      let trackReceivedCount = 0;
      this.wowzaPeerConnection.gotRemoteTrack = (event) => {
        trackReceivedCount++;
        console.log(`WebRTCPlayer: gotRemoteTrack called (track #${trackReceivedCount})`, {
          destroyed: this.destroyed,
          hasStreams: event.streams && event.streams.length > 0,
          trackKind: event.track?.kind,
          trackId: event.track?.id,
          trackEnabled: event.track?.enabled,
          trackReadyState: event.track?.readyState,
        });

        if (this.destroyed) {
          console.warn('WebRTCPlayer: Component destroyed, ignoring track');
          return;
        }
        
        const eventStream = event.streams[0];
        if (!eventStream) {
          console.warn('WebRTCPlayer: No stream in event.streams[0]');
          return;
        }

        console.log('WebRTCPlayer: Processing stream', {
          streamId: eventStream.id,
          streamActive: eventStream.active,
          tracksCount: eventStream.getTracks().length,
          tracks: eventStream.getTracks().map(t => ({
            kind: t.kind,
            enabled: t.enabled,
            readyState: t.readyState,
            muted: t.muted,
          })),
        });

        this.stream = eventStream;
        this.$emit('track', event.track, eventStream);
        
        // Call original to assign stream to video element
        console.log('WebRTCPlayer: Calling original gotRemoteTrack to assign stream to video element');
        try {
          originalGotRemoteTrack(event);
          console.log('WebRTCPlayer: Stream assigned to video element successfully');
        } catch (error) {
          console.error('WebRTCPlayer: Error assigning stream to video element', error);
        }
        
        // Wait for all tracks to be added before trying to play
        // Use setTimeout with 0 delay to ensure DOM is updated and all tracks are processed
        setTimeout(() => {
          if (this.destroyed) {
            console.warn('WebRTCPlayer: Component destroyed in setTimeout');
            return;
          }
          
          const video = this.$refs.video;
          if (!video) {
            console.error('WebRTCPlayer: Video element not found in setTimeout');
            return;
          }
          
          console.log('WebRTCPlayer: Checking video element state before play attempt', {
            srcObject: !!video.srcObject,
            srcObjectActive: video.srcObject?.active,
            srcObjectTracks: video.srcObject?.getTracks().length,
            paused: video.paused,
            muted: video.muted,
            readyState: video.readyState,
            currentTime: video.currentTime,
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
          });
          
          // Only try to play if we have a valid srcObject with active tracks
          if (!video.srcObject) {
            console.warn('WebRTCPlayer: No srcObject on video element, cannot play');
            return;
          }

          const tracks = video.srcObject.getTracks();
          const activeTracks = tracks.filter(t => t.readyState === 'live');
          console.log('WebRTCPlayer: Video tracks status', {
            totalTracks: tracks.length,
            activeTracks: activeTracks.length,
            tracks: tracks.map(t => ({
              kind: t.kind,
              enabled: t.enabled,
              readyState: t.readyState,
              muted: t.muted,
            })),
          });

          if (activeTracks.length === 0) {
            console.warn('WebRTCPlayer: No active tracks, waiting...');
            // Wait a bit more for tracks to become active
            setTimeout(() => {
              if (this.destroyed || !video.srcObject) return;
              const newActiveTracks = video.srcObject.getTracks().filter(t => t.readyState === 'live');
              if (newActiveTracks.length > 0) {
                console.log('WebRTCPlayer: Tracks are now active, attempting play');
                this._attemptVideoPlay(video, eventStream);
              } else {
                console.warn('WebRTCPlayer: Still no active tracks after wait');
              }
            }, 500);
            return;
          }
          
          this._attemptVideoPlay(video, eventStream);
        }, 100);
      };

      // Set peerConnectionConfig if provided
      if (this.peerConfig && this.peerConfig.iceServers) {
        console.log('WebRTCPlayer: Setting peerConnectionConfig', this.peerConfig);
        this.wowzaPeerConnection.peerConnectionConfig = this.peerConfig;
      }

      // Start the connection
      console.log('WebRTCPlayer: Starting WowzaPeerConnectionPlay');
      this.wowzaPeerConnection.start();
    },

    stop() {
      return new Promise((resolve) => {
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
        }
        if (this.wowzaPeerConnection) {
          this.wowzaPeerConnection.stop();
          this.wowzaPeerConnection = null;
        }
        resolve();
      });
    },

    isPlaying() {
      return !!this.wowzaPeerConnection && 
             this.wowzaPeerConnection.peerConnection && 
             this.wowzaPeerConnection.peerConnection.connectionState !== 'closed';
    },

    _onConnectionStateChange(event) {
      if (this.destroyed) {
        console.warn('WebRTCPlayer: Component destroyed, ignoring connection state change');
        return;
      }
      
      const peerConnection = event.target || this.wowzaPeerConnection?.peerConnection;
      if (!peerConnection) {
        console.warn('WebRTCPlayer: No peerConnection in connection state change');
        return;
      }

      const connectionState = peerConnection.connectionState;
      const iceConnectionState = peerConnection.iceConnectionState;
      console.log('WebRTCPlayer: RTCPeerConnection state changed', {
        connectionState: connectionState,
        iceConnectionState: iceConnectionState,
        signalingState: peerConnection.signalingState,
      });

      switch (connectionState) {
        case 'connecting':
          console.log('WebRTCPlayer: Connection connecting...');
          break;
        case 'connected':
          console.log('WebRTCPlayer: Connection connected!');
          break;
        case 'disconnected':
        case 'failed':
        case 'closed':
          const error = new Error('connectionState ' + connectionState);
          error._type = 'connectionState';
          console.error('WebRTCPlayer: Connection error', error);
          this.$emit('peerConnectionClosed', error._type);
          this._handleError(error);
          break;
      }
    },

    _onError(error) {
      if (this.destroyed) return;
      console.error('WebRTCPlayer: Error from WowzaPeerConnectionPlay', error);
      this.$emit('error', error);
      this._handleError(error);
    },

    _onStop() {
      console.log('WebRTCPlayer: Connection stopped');
      this.stream = null;
    },

    _attemptVideoPlay(video, eventStream) {
      // Try unmuted first for better user experience
      video.muted = false;
      console.log('WebRTCPlayer: Attempting to play video unmuted', {
        muted: video.muted,
        paused: video.paused,
        readyState: video.readyState,
      });

      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.isMuted = video.muted;
            console.log('WebRTCPlayer: ✅ Video playing successfully with audio!', {
              paused: video.paused,
              muted: video.muted,
              currentTime: video.currentTime,
              videoWidth: video.videoWidth,
              videoHeight: video.videoHeight,
              readyState: video.readyState,
            });
            
            // Emit events for backwards compatibility
            this.$emit('stream', eventStream);
            this.$emit('connect');
          })
          .catch((e) => {
            console.warn('WebRTCPlayer: Unmuted autoplay blocked, falling back to muted', {
              error: e,
              name: e.name,
              message: e.message,
            });
            
            // Autoplay blocked - fall back to muted
            video.muted = true;
            this.isMuted = true;
            return video.play();
          })
          .then(() => {
            if (video.muted) {
              console.log('WebRTCPlayer: ✅ Video playing successfully (muted fallback)', {
                paused: video.paused,
                muted: video.muted,
                currentTime: video.currentTime,
                videoWidth: video.videoWidth,
                videoHeight: video.videoHeight,
                readyState: video.readyState,
              });
            }
            
            // Emit events for backwards compatibility
            this.$emit('stream', eventStream);
            this.$emit('connect');
          })
          .catch((e) => {
            console.error('WebRTCPlayer: ❌ Failed to play video even muted', {
              error: e,
              name: e.name,
              message: e.message,
              paused: video.paused,
              readyState: video.readyState,
              srcObject: !!video.srcObject,
              srcObjectActive: video.srcObject?.active,
            });
            
            // Still emit stream event even if play failed
            this.$emit('stream', eventStream);
          });
      } else {
        console.warn('WebRTCPlayer: play() returned undefined (may be synchronous)');
        // Emit events anyway
        this.$emit('stream', eventStream);
        this.$emit('connect');
      }
    },

    _handleError(error) {
      console.error('WebRTCPlayer: Error occurred', error);
      this.$emit('error', error);

      this.stop().finally(() => {
        if (this.destroyed) {
          console.log('WebRTCPlayer: Component destroyed, not reconnecting');
          return;
        }
        console.log('WebRTCPlayer: Reconnecting in 5 seconds...');
        this.reconnectTimeout = setTimeout(() => {
          if (this.destroyed) return;
          console.log('WebRTCPlayer: Reconnecting...');
          this.destroyed = false;
          this.play();
        }, 5000);
      });
    },

    onVideoLoadedMetadata() {
      const video = this.$refs.video;
      console.log('WebRTCPlayer: Video loadedmetadata', {
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        duration: video.duration,
        readyState: video.readyState,
      });
    },
    
    onVideoPlaying() {
      const video = this.$refs.video;
      console.log('WebRTCPlayer: Video is now playing!', {
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        paused: video.paused,
        currentTime: video.currentTime,
      });
    },
    
    onVideoError(e) {
      const video = this.$refs.video;
      console.error('WebRTCPlayer: Video error', {
        error: video.error,
        code: video.error?.code,
        message: video.error?.message,
      });
    },

    toggleMute() {
      const video = this.$refs.video;
      if (video) {
        video.muted = !video.muted;
        this.isMuted = video.muted;
      }
    },

    destroy() {
      this.destroyed = true;

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      if (this.wowzaPeerConnection) {
        this.wowzaPeerConnection.stop();
        this.wowzaPeerConnection = null;
      }

      if (this.stream && this.stream.getTracks) {
        this.stream.getTracks().forEach((track) => track.stop());
      }
      this.stream = null;
    },
  },
};
</script>

<style lang="scss">
.webrtc-player {
  position: relative;
  width: 100%;
  height: 100%;

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: black;
  }

  .vumeter-container {
    position: absolute;
    bottom: 10px;
    right: 10px;
    width: 40px;
    height: 100px;
  }

  .mute-btn {
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.6);
    border: none;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    cursor: pointer;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(0, 0, 0, 0.8);
    }
  }
}
</style>

